import { Button } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { processContentForEditor } from 'src/components/primatives/sf-rich-text/rich-text-utils'

import '../sf-rich-text/sf-rich-text.css'

function parseInlineCssToReactStyle(css: string): CSSProperties {
  const out: Record<string, string> = {}
  for (const part of css.split(';')) {
    const trimmed = part.trim()
    const colon = trimmed.indexOf(':')
    if (colon === -1) continue
    const key = trimmed.slice(0, colon).trim()
    const value = trimmed.slice(colon + 1).trim()
    const camelKey = key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
    out[camelKey] = value
  }
  return out as CSSProperties
}

export interface SFRichTextViewerProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'dangerouslySetInnerHTML'> {
  content: string
  minHeight?: string
  maxHeight?: string
  editorStyles?: string
  lineClamp?: number
  readMoreText?: string
  mentionColor?: string
  linkColor?: string
}

export interface SFRichTextViewerRef {
  /** Root element that wraps the rendered HTML (matches prior ProseMirror root). */
  getEditorElement: () => HTMLElement | null
}

const SFRichTextViewer = forwardRef<SFRichTextViewerRef, SFRichTextViewerProps>((props, forwardedRef) => {
  const {
    content,
    className,
    minHeight = '0rem',
    maxHeight,
    editorStyles = '',
    lineClamp,
    readMoreText = 'Read More...',
    mentionColor = 'var(--primary)',
    linkColor = 'var(--primary)',
    ...rest
  } = props

  const innerRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLineClamped, setIsLineClamped] = useState(false)
  const [calculatedMaxHeight, setCalculatedMaxHeight] = useState<string | undefined>(maxHeight as string | undefined)

  const processedContent = processContentForEditor(content as string)

  useImperativeHandle(forwardedRef, () => ({
    getEditorElement: () => innerRef.current,
  }))

  const getMaxHeight = () => {
    if (isExpanded) {
      return
    }
    if (calculatedMaxHeight !== undefined) {
      return calculatedMaxHeight
    }
    if (maxHeight !== undefined) {
      return maxHeight as string
    }
  }

  const currentMaxHeight = getMaxHeight()

  const contentStyle: CSSProperties = {
    minHeight: minHeight as string,
    padding: '0px',
    border: 'none',
    ...(currentMaxHeight !== undefined && currentMaxHeight !== '' ? { maxHeight: currentMaxHeight } : {}),
    ...(lineClamp && !isExpanded ? { overflow: 'hidden' } : {}),
    ...parseInlineCssToReactStyle(editorStyles as string),
    ...(mentionColor ? { ['--sf-richtext-mention-color' as string]: String(mentionColor) } : {}),
    ...(linkColor ? { ['--sf-richtext-link-color' as string]: String(linkColor) } : {}),
  }

  useEffect(() => {
    const el = innerRef.current
    if (el) {
      el.style.setProperty('--sf-richtext-mention-color', mentionColor as string)
      el.style.setProperty('--sf-richtext-link-color', linkColor as string)
    }
  }, [mentionColor, linkColor])

  useEffect(() => {
    if (!lineClamp) {
      setCalculatedMaxHeight(maxHeight as string | undefined)
      return
    }

    if (isExpanded) {
      return
    }

    const calculateMaxHeight = () => {
      const el = innerRef.current
      if (el) {
        const computedStyle = window.getComputedStyle(el)
        const lineHeight = Number.parseFloat(computedStyle.lineHeight)
        const fontSize = Number.parseFloat(computedStyle.fontSize)
        const effectiveLineHeight: number =
          !Number.isNaN(lineHeight) && lineHeight > 0
            ? lineHeight * 0.85 /* 0.85 was found through trial and error to get the correct height */
            : !Number.isNaN(fontSize) && fontSize > 0
              ? fontSize * 1.5
              : 20
        const calculatedHeight = effectiveLineHeight * (lineClamp as number)
        setCalculatedMaxHeight(`${calculatedHeight}px`)
        return true
      }
      return false
    }

    if (!calculateMaxHeight()) {
      const timeoutId = setTimeout(() => {
        calculateMaxHeight()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [content, lineClamp, isExpanded, maxHeight])

  useEffect(() => {
    if (!lineClamp) {
      setIsLineClamped(false)
      return
    }

    setIsExpanded(false)

    const observer = new ResizeObserver((entries) => {
      const target = entries[0].target
      setIsLineClamped(target.scrollHeight > target.clientHeight)
    })

    const setupObserver = () => {
      const el = innerRef.current
      if (el) {
        observer.observe(el)
        return true
      }
      return false
    }

    if (!setupObserver()) {
      const timeoutId = setTimeout(() => {
        setupObserver()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        observer.disconnect()
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [lineClamp, content])

  return (
    <div className={cn('w-full', className)} {...rest}>
      <div
        className="ProseMirror border-none px-0 py-1 text-default outline-none focus:outline-none"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: read-only rich text HTML from stored editor output
        dangerouslySetInnerHTML={{ __html: processedContent }}
        ref={innerRef}
        style={contentStyle}
      />
      {lineClamp && isLineClamped && !isExpanded && (
        <div className="mt-2 flex w-full justify-end">
          <Button
            className="h-auto p-0 text-muted-foreground text-sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsExpanded(true)
            }}
            type="button"
            variant="link"
          >
            {readMoreText as string}
          </Button>
        </div>
      )}
    </div>
  )
})

SFRichTextViewer.displayName = 'SFRichTextViewer'

export default SFRichTextViewer
